import { css } from '@emotion/react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type ReactNode,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { hexColorWithOpacityToString } from '../colorPicker/colorPicker.helpers';

type FloatingBoxProps = Readonly<{
  children: ReactNode;
  className?: string;
  gap?: number;
  heading?: string;
  isOpen: boolean;

  onClose?: () => void;
}>;

const wrapperStyle = (theme: Theme) => css({
  position: 'relative',
  background: hexColorWithOpacityToString(theme.modalColors.contentBackground, .7),
  borderRadius: 4,
  boxShadow: `0 0 0 2px ${theme.buttonColors.secondaryBackground}`,
  marginTop: 2, // Makes the shadow included in the parent layout-wise

  '&:first-of-type': {
    marginLeft: 2, // Makes the shadow included in the parent layout-wise
  },
  fontSize: 16,
});

const closeButtonStyle = (theme: Theme) => css({
  color: theme.textColors.primary,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 20,
  height: 34,
  width: 34,
  lineHeight: '34px',
  textAlign: 'center',
  outline: 'none',
});

const contentStyle = css({
  position: 'relative',
  padding: '22px',
});

const headingStyle = (theme: Theme) => css({
  lineHeight: 1.25,
  padding: '9px 10px 6px 10px',
  margin: 0,
  background: theme.iconColors.backgroundPrimary,
  color: theme.textColors.primary,
  borderRadius: '4px 0',
  display: 'inline-block',
  fontWeight: 700,
  fontSize: 16,
});

const topBarStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
});

export const FloatingBoxComponent: FC<FloatingBoxProps> = (props) => {
  return (
    <div
      className={props.className}
      css={wrapperStyle}
    >
      <div css={topBarStyle}>
        {props.heading &&
          <h2 css={headingStyle}>{props.heading}</h2>
        }

        {props.onClose && (
          <button
            css={closeButtonStyle}
            onClick={props.onClose}
            aria-label={`Close panel - ${props.heading}`}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </div>

      <Stack
        css={contentStyle}
        gap={props.gap}
      >
        {props.children}
      </Stack>
    </div>
  );
};
